import React, { useEffect } from "react";
import {
    Grid,
    Box,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Typography,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";

const Aitools = ({ formData, setFormData, errors, setAitoolsErrors }) => {
    // Ensure ai_tools is always an array
    const getAITools = () => {
        if (Array.isArray(formData?.ai_tools)) {
            return formData.ai_tools;
        }

        if (typeof formData?.ai_tools === "string") {
            try {
                const parsed = JSON.parse(formData.ai_tools);
                setFormData({
                    ...formData,
                    ai_tools: Array.isArray(parsed) ? parsed : []
                });
                return Array.isArray(parsed) ? parsed : [];
            } catch (e) {
                console.error("Error parsing ai_tools:", e);
                return [];
            }
        }

        return [];
    };

    const aiTools = getAITools();

    // Automatically add one empty tool when array is empty
    useEffect(() => {
        if (aiTools.length === 0) {
            const newTool = {
                tool_name: "",
                purpose_function: "",
                version: "",
            };

            const updatedTools = [newTool];
            setFormData({
                ...formData,
                ai_tools: updatedTools,
            });
        }
    }, [aiTools.length]); // Only run when the length changes

    console.log({ formData });
    console.log({ aiTools });

    const handleAddTool = () => {
        const newTool = {
            tool_name: "",
            purpose_function: "",
            version: "",
        };

        const updatedTools = [...aiTools, newTool];
        setFormData({
            ...formData,
            ai_tools: updatedTools,
        });
    };

    const handleRemoveTool = (index) => {
        const updatedTools = aiTools.filter((_, i) => i !== index);
        setFormData({
            ...formData,
            ai_tools: updatedTools,
        });

        // Clear errors for removed tool
        if (errors && setAitoolsErrors) {
            const newErrors = { ...errors };
            delete newErrors[`ai_tools_${index}_tool_name`];
            delete newErrors[`ai_tools_${index}_purpose_function`];
            delete newErrors[`ai_tools_${index}_version`];
            setAitoolsErrors(newErrors);
        }
    };

    const handleToolChange = (index, field, value) => {
        const updatedTools = aiTools.map((tool, i) =>
            i === index ? { ...tool, [field]: value } : tool
        );

        setFormData({
            ...formData,
            ai_tools: updatedTools,
        });

        // Clear error for this field when user starts typing
        if (errors && setAitoolsErrors && errors[`ai_tools_${index}_${field}`]) {
            const newErrors = { ...errors };
            delete newErrors[`ai_tools_${index}_${field}`];
            setAitoolsErrors(newErrors);
        }
    };

    return (
        <>
            <div className="card p-4">

                <h5>
                    AI Tools & Platforms Used <span className="text-danger">*</span>
                </h5>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    List all AI tools, platforms, and software used in your project

                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Example: Runway, Pika, Midjourney, ChatGPT, ElevenLabs, Adobe Firefly, etc.

                </Typography>
            </div>

            {aiTools.length === 0 ? (
                <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ textAlign: "center", py: 4 }}
                >
                    Loading AI tools...
                </Typography>
            ) : (
                <TableContainer component={Paper} sx={{ mb: 3 }}>
                    <Table sx={{ minWidth: 650 }} aria-label="AI Tools table">
                        <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                            <TableRow>
                                <TableCell width="5%">#</TableCell>
                                <TableCell width="30%">AI Tool / Platform Name *</TableCell>
                                <TableCell width="40%">Purpose / Function *</TableCell>
                                <TableCell width="20%">Version</TableCell>
                                <TableCell width="5%">
                                    <Button variant="outlined" onClick={handleAddTool}>
                                        Add
                                    </Button>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {aiTools.map((tool, index) => (
                                <TableRow key={index}>
                                    <TableCell>{index + 1}</TableCell>
                                    <TableCell>
                                        <TextField
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            required
                                            value={tool.tool_name || ""}
                                            onChange={(e) =>
                                                handleToolChange(index, "tool_name", e.target.value)
                                            }
                                            error={!!errors?.[`ai_tools_${index}_tool_name`]}
                                            helperText={errors?.[`ai_tools_${index}_tool_name`]}
                                            placeholder="e.g., Midjourney, Runway ML"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            required
                                            value={tool.purpose_function || ""}
                                            onChange={(e) =>
                                                handleToolChange(
                                                    index,
                                                    "purpose_function",
                                                    e.target.value
                                                )
                                            }
                                            error={!!errors?.[`ai_tools_${index}_purpose_function`]}
                                            helperText={
                                                errors?.[`ai_tools_${index}_purpose_function`]
                                            }
                                            placeholder="e.g., Image generation, Script writing"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            value={tool.version || ""}
                                            onChange={(e) =>
                                                handleToolChange(index, "version", e.target.value)
                                            }
                                            error={!!errors?.[`ai_tools_${index}_version`]}
                                            helperText={errors?.[`ai_tools_${index}_version`]}
                                            placeholder="e.g., v2.1, GPT-4"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {/* Show delete button only when there's more than 1 tool */}
                                        {aiTools.length > 1 && (
                                            <IconButton
                                                onClick={() => handleRemoveTool(index)}
                                                color="error"
                                                size="small"
                                            >
                                                <Delete />
                                            </IconButton>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Validation summary */}
            {errors?.ai_tools_common && (
                <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                    {errors.ai_tools_common}
                </Typography>
            )}
        </>
    );
};

export default Aitools;
